import { PerformanceDataProps } from "./ProfilePage";

export interface UpcomingPerformance {
  posterImageType?: string;
  idx: number;
  titleEn?: string;
  titleLocal?: string;
  startAtUtc?: string;
  endAtUtc?: string;
  startTime?: string;
  endTime?: string;
  timezone?: string;
  startPrecision?: string;
  endPrecision?: string;
  hasDate?: boolean;
  isOpenRun?: boolean;
  role?: string;
}

export interface ProfileAward {
  id: string;
  category: string;
  ceremony: string;
  year: number;
  result: string;
  performanceTitle?: string;
  performanceIdx?: number;
}

export interface ProfileSpotlight {
  id: string;
  thumbnailUrl: string;
  caption?: string;
}

// 공개 API 응답을 정규화한 화면 데이터
export interface ProfileContent {
  upcoming?: UpcomingPerformance[];
  awards?: ProfileAward[];
  awardsTruncated?: boolean;
  spotlights?: ProfileSpotlight[];
}

export const ITDB_ENDPOINT = (process.env.REACT_APP_ITDB_WEB_ENDPOINT || "https://internationaltheatredatabase.com").replace(/\/+$/, "");

export function safeWebUrl(value?: string) {
  if (!value?.trim()) return undefined;
  try {
    const trimmed = value.trim();
    const url = new URL(/^[a-z][a-z\d+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`);
    return ["https:", "http:"].includes(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
}

// SNS 아이디만 저장된 링크의 서비스별 주소 보완 및 명시된 URL 유지
export function profileLinkUrl(value?: string, type?: string) {
  const input = value?.trim();
  if (!input) return undefined;
  if (/^[a-z][a-z\d+.-]*:/i.test(input) || /^(?:www\.|(?:[a-z\d-]+\.)?(?:instagram\.com|youtube\.com|youtu\.be|facebook\.com|tiktok\.com|imdb\.com|ibdb\.com)(?:\/|$))/i.test(input)) return safeWebUrl(input);
  const handle = input.replace(/^@/, "");
  if (!handle || /[\s?#]/.test(handle)) return undefined;
  switch (type) {
    case "IG": return safeWebUrl(`https://www.instagram.com/${handle}`);
    case "YT": return safeWebUrl(`https://www.youtube.com/${handle.startsWith("UC") ? "channel/" : /^(channel|c|user)\//.test(handle) ? "" : "@"}${handle}`);
    case "FB": return safeWebUrl(`https://www.facebook.com/${handle}`);
    case "TIKTOK": return safeWebUrl(`https://www.tiktok.com/@${handle}`);
    default: return safeWebUrl(input);
  }
}

type PerformanceDates = Pick<PerformanceDataProps, "startTime" | "endTime" | "startAtUtc" | "endAtUtc" | "startPrecision" | "endPrecision" | "isOpenRun" | "timezone"> & { hasDate?: boolean };

export function formatPerformanceDates(performance: PerformanceDates) {
  if (performance.hasDate === false) return "Dates to be announced";
  const format = (local?: string, utc?: string, precision?: string) => {
    if (precision === "none" || precision === "unknown") return "";
    const value = local || utc;
    if (!value) return "";
    // 현지 날짜 우선 사용 및 UTC 응답의 공연 시간대 적용
    const date = new Date(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`);
    if (Number.isNaN(date.getTime())) return "";
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      ...(precision !== "year" && { month: "short" }),
      ...(!["year", "month"].includes(precision || "") && { day: "numeric" }),
      timeZone: local ? "UTC" : performance.timezone || "UTC",
    };
    try { return new Intl.DateTimeFormat("en-US", options).format(date); }
    catch { return new Intl.DateTimeFormat("en-US", { ...options, timeZone: "UTC" }).format(date); }
  };
  const start = format(performance.startTime, performance.startAtUtc, performance.startPrecision);
  const end = format(performance.endTime, performance.endAtUtc, performance.endPrecision);
  if (performance.isOpenRun) return start ? `${start} – Open run` : "Open run";
  if (!start) return end ? `Until ${end}` : "";
  return end && end !== start ? `${start} – ${end}` : start;
}
